import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";

export type SystemAdminRole = "SUPER_ADMIN" | "SUPPORT" | "READ_ONLY";

export interface Me {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  role: "OWNER" | "TEAM";
  timezone: string;
  createdAt: string;
  lastActiveProjectId: string | null;
  lastActiveWorkspaceId: string | null;
  // Phase A: true when this user has a SystemAdmin row. Drives the
  // visibility of the "Admin panel" link in the topbar profile menu.
  // MFA state is intentionally NOT exposed here — that's behind the
  // /api/sysadmin/auth/me endpoint so a leaked /me payload doesn't
  // reveal enrollment progress.
  isSystemAdmin: boolean;
  systemAdminRole: SystemAdminRole | null;
}

export function useMe() {
  return useQuery<Me | null>({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api<Me>("/api/auth/me");
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ ok: true }>("/api/auth/logout", { method: "POST" }),
    onSuccess: () => {
      qc.setQueryData(["me"], null);
      qc.invalidateQueries();
      window.location.href = "/login";
    },
  });
}

export function useUpdateDisplayName() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (displayName: string) =>
      api<{ id: string; displayName: string }>("/api/auth/me", {
        method: "PATCH",
        body: JSON.stringify({ displayName }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useUpdateTimezone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (timezone: string) =>
      api<{ id: string; timezone: string }>("/api/auth/me", {
        method: "PATCH",
        body: JSON.stringify({ timezone }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}
